import { Box, Button, Fade, Popper, createSvgIcon, Divider, Typography } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import ViewColumnRoundedIcon from '@mui/icons-material/ViewColumnRounded';
import { FactoryRounded } from "@mui/icons-material";
import api from "../services/ApiService";
import { ApiResponse } from "../models/ApiResponse";
import { toast } from "react-toastify";
import moment from "moment";
import SessionService from "../services/SessionService";
import { useAppSelector } from "../store";
import { Permissions, } from "../models/Enum";
import { maxRecordsWarningMessage, SAVED_PRODUCT_EXPORT_KEY } from "../utils/constants";

interface ExportProductListingProps {
    selectedRows: any[];
    productsTotalCount: number;
    selectAll: boolean;
    setLoading: (loading: boolean) => void;
    onOpenChooseColumns: () => void;
    ensureAllExportFieldsLoaded: () => Promise<string[]>;
};

type ExportFileFormat =
    | 'Product List'
    | 'Products With Image'
    | 'Inriver Pim'
    | 'JDE Components'
    | 'JDE SellingLine Items';

const ExportProductListing = (props: ExportProductListingProps) => {
    const { selectedRows, selectAll, productsTotalCount, setLoading, onOpenChooseColumns, ensureAllExportFieldsLoaded } = props;
    const ExportIcon = createSvgIcon(
        <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z" />,
        'SaveAlt',
    );

    const itemListRequestState = useAppSelector((state) => state.items.itemListRequest);
    const productListFilterValuesSelected = useAppSelector((state) => state.filters.productListView.selectedFilterValues);

    const [popperAnchorEl, setPopperAnchorEl] = useState<null | HTMLElement>(null);
    const [xlsxOptionsOpen, setXlsxOptionsOpen] = useState(false);
    const [xlsxOptionsType, setXlsxOptionsType] = useState<null | "productList" | "withImage">(null);
    const [xlsxOptionsPos, setXlsxOptionsPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    const popperContentRef = useRef<HTMLDivElement | null>(null);
    const xlsxOptionsRef = useRef<HTMLDivElement | null>(null);

    const sessionService = SessionService.getInstance();

    const handlePopper = (event: React.MouseEvent<HTMLElement>) => {
      setPopperAnchorEl(popperAnchorEl ? null : event.currentTarget);
    };

    //openPopper check if the popper is open or not;
    const openPopper = Boolean(popperAnchorEl);

    //needed for when use click out of popper
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const popperContainer = document.querySelector('.MuiPopper-root');
            const modalDialog = document.querySelector('[role="dialog"]');

            const insidePopper = popperContainer?.contains(target);
            const insideModal = modalDialog?.contains(target);

            if (!insidePopper && !insideModal) {
                setPopperAnchorEl(null);
                setXlsxOptionsOpen(false);
                setXlsxOptionsType(null);
            }
        };

        if (openPopper) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        } return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openPopper, popperAnchorEl]);

    const loadSavedSelectedFields = () => {
        try {
            const raw = globalThis.localStorage.getItem(SAVED_PRODUCT_EXPORT_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.every((x) => typeof x === "string")) return parsed as string[];
            return null;
        } catch {
            return null;
        }
    };

    const isAllFieldsSelected = (selected: string[], all: string[]) => {
        if (!selected?.length || !all?.length) return false;
        const selSet = new Set(selected.map((s) => s.toLowerCase()));
        if (selSet.size !== all.length) return false;
        return all.every((a) => selSet.has(a.toLowerCase()));
    };

    // Export job updates are handled globally in `Layout.tsx` so that downloads
    // still work even when the user navigates away from this page.
    
    const fetchItemIds = async () => {
        const response = await api.post<ApiResponse<any>>('/Item/get-product-id-list-filter', itemListRequestState)
        if(response.isSuccess) {
            return response?.data || [];
        }
    }

    const downloadFile = (blobData: Blob, fileName: string) => {
        const date = moment();
        const formatedate = date.format('YYYYMMDDHHMMSS')
    
        //Create a download link and trigger the download
        const blobURL = window.URL.createObjectURL(blobData);
        const a = document.createElement('a');
        a.href = blobURL;
        a.style.display = 'none';
        //Add link element to inititate the download
        document.body.appendChild(a);
        a.download = fileName+formatedate;
        //click the link element to initiate the dowload
        a.click();
        a.remove();
        URL.revokeObjectURL(blobURL);
    };

    const invokeDownload = async (requestInput: any, inputFileName, inputFileFormat) => {
      try{
        const sessionInfo = sessionService.getSessionToken();
        let url = '';
        let requestBody = requestInput;

          switch (inputFileFormat) {
              case 'BOM JDE':
                  url = `${process.env.REACT_APP_API_URL}/Item/export-bom-csv-jde-import`;
                  break;
              case 'Factory Sample':
                  url = `${process.env.REACT_APP_API_URL}/Item/factory-sample-request`;
                  requestBody = { productIds: requestInput };
                  break;
              case 'Product List':
                  url = `${process.env.REACT_APP_API_URL}/Item/export-product-list-async`;
                  // New payload expects: { ids: [], columns: null|string[] }
                  if (Array.isArray(requestInput)) {
                      requestBody = { ids: requestInput, columns: null };
                  }
                  break;
              case 'Products With Image':
                  url = `${process.env.REACT_APP_API_URL}/Item/export-products-with-image-async`;
                  // New payload expects: { ids: [], columns: null|string[] }
                  if (Array.isArray(requestInput)) {
                      requestBody = { ids: requestInput, columns: null };
                  }
                  break;
              case 'Inriver Pim':
                  url = `${process.env.REACT_APP_API_URL}/Item/inriver-pim-export`;
                  break;
              case 'JDE Components':
                  url = `${process.env.REACT_APP_API_URL}/Component/jde-export`;
                  break;
              case 'JDE SellingLine Items':
                  url = `${process.env.REACT_APP_API_URL}/Item/jde-export`;
                  break;
              default:
                  throw new Error('Invalid download type');
          }
        
        setLoading(true);
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + sessionInfo,
          },
          body: JSON.stringify(requestBody)
        });

        const contentType = response.headers.get("content-type");
        let fileName = inputFileName;
          if (response.ok) {
            if(inputFileFormat === "Factory Sample") {
                const bodyResp = await response.json();
                
                if (bodyResp.isSuccess) {
                    const fileName = bodyResp.data;
              
                    // Open new window for download
                    window.open(`${process.env.REACT_APP_API_URL}/Item/download-factorysample-zip?fileName=${fileName}`, '_blank');
                    toast.success(`${inputFileFormat} Exported Successfully`);
                }
                else {
                    toast.error(bodyResp.message);
                }

                setLoading(false);
            }
            else if (contentType && contentType.includes('application/json')) {
                const bodyResp = await response.json();
                if(bodyResp.isSuccess) {
                    toast.info(bodyResp.message);
                }
                else {
                    toast.error(bodyResp.message);
                }
                setLoading(false);
            } else if (contentType && (contentType.includes('text/csv') ||
                contentType.includes('application/zip') ||
                contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'))) {
                const blobData = await response.blob();
                downloadFile(blobData, fileName);
                toast.success(`${inputFileFormat} Exported Successfully`);
                setLoading(false);
            }
        } 
        else if (response.status === 404) {
            toast.error("No data found");
            setLoading(false);
        } else {
            setLoading(false);
            throw new Error();
        }
      }catch (error) {
        setLoading(false);
        console.error('Error while downloading file:', error);
        toast.error('Error while downloading file'); 
      }
    };

    const invokeAsyncExport = async (requestInput: any, inputFileName: string, inputFileFormat: ExportFileFormat) => {
        try {
            const sessionInfo = sessionService.getSessionToken();
            let url = '';

            switch (inputFileFormat) {
                case 'Product List':
                    url = `${process.env.REACT_APP_API_URL}/Item/export-product-list-async`;
                    break;
                case 'Products With Image':
                    url = `${process.env.REACT_APP_API_URL}/Item/export-products-with-image-async`;
                    break;
                case 'Inriver Pim':
                    url = `${process.env.REACT_APP_API_URL}/Item/inriver-pim-export-async`;
                    break;
                case 'JDE Components':
                    url = `${process.env.REACT_APP_API_URL}/Component/jde-export-async`;
                    break;
                case 'JDE SellingLine Items':
                    url = `${process.env.REACT_APP_API_URL}/Item/jde-export-async`;
                    break;
                default:
                    throw new Error('Invalid async export type');
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + sessionInfo,
                },
                body: JSON.stringify(requestInput)
            });

            if (!response.ok) {
                if (response.status === 404) {
                    toast.error("No data found");
                    return;
                }
                throw new Error('Unable to start export job');
            }

            const bodyResp = await response.json();
            if (bodyResp?.isSuccess && bodyResp?.data?.jobId) {
                toast.info(`${inputFileFormat} export started. You can continue working and will be notified when it is ready.`);
            } else {
                toast.error(bodyResp?.message || 'Unable to start export');
            }
        } catch (error) {
            console.error('Error while starting export:', error);
            toast.error('Error while starting export');
        }
    };

    const getProductIdsForExport = async (selectedProductIds) => {
        return selectAll
            ? await fetchItemIds()
            : !selectAll && selectedProductIds.length > 0
            ? selectedProductIds : await fetchItemIds();
    }

    const handleCSVforJDE = async () => {
        let selectedId = selectedRows.map(i => i.id);
        let fileName = 'BOM_JDE_';
        let fileFormat = 'BOM JDE'

        if(selectAll){
            const idListResponse = await fetchItemIds();
            await invokeDownload(idListResponse, fileName, fileFormat)
        }else if(selectedRows.length > 0) {
            await invokeDownload(selectedId,fileName, fileFormat);
        } else {
            toast.error("Select a Product or Select All to Export");
        }
    };

    const handleFactorySample = async () => {
        let fileName = 'FactorySample_';
        let fileFormat = 'Factory Sample';
        let selectedProductIds = selectedRows.map(row => row.id);
        let selectedStockCodes = selectedRows.map(row => row.pdStockCode);
        
        if ((selectAll && productsTotalCount <= 0) || (!selectAll && selectedProductIds?.length <= 0)) {
            return toast.error("There is no PDStockCode, Please add record that have PDStockCode");
        }

        if (selectAll) {
            if (productsTotalCount > 25) {
                return toast.error(maxRecordsWarningMessage(25));
            } else {
                const productIds = await getProductIdsForExport(selectedProductIds);
                await invokeDownload(productIds, fileName, fileFormat);
            }
        } else if (!selectAll && selectedProductIds?.length > 0) {
            if (selectedProductIds?.length > 25) {
                return toast.error(maxRecordsWarningMessage(25));
            } else if (selectedProductIds?.length <= 25) {
                if (selectedStockCodes.some(value => value === null || value === '')) {
                    toast.error("There are Record with no PDStockCode, Please Deselect Record with no PDStockCode");
                } else {
                    await invokeDownload(selectedProductIds, fileName, fileFormat);
                }
            }
        }
    };

    const handleCSVforProductList = async () => {
        let selectedProductIds = selectedRows.map(row => row.id);
        let fileName = 'Product_List_';

        const productIds = await getProductIdsForExport(selectedProductIds);
        await invokeAsyncExport({ ids: productIds, columns: null }, fileName, 'Product List');
    };

    const handleCSVforProductListSelectedFields = async () => {
        const selectedProductIds = selectedRows.map(row => row.id);
        const fileName = 'Product_List_';
        const fileFormat = 'Product List';

        const saved = loadSavedSelectedFields();
        const productIds = await getProductIdsForExport(selectedProductIds);
        const allFields = await ensureAllExportFieldsLoaded();
        const columns = isAllFieldsSelected(saved, allFields) ? null : saved;
        const requestPayload = { ids: productIds, columns };
        await invokeDownload(requestPayload, fileName, fileFormat);
    };

    const handleExportProductsWithImage = async () => {
        let selectedProductIds = selectedRows.map(row => row.id);
        let fileName = 'Products_With_Image_';

        const productIds = await getProductIdsForExport(selectedProductIds);
        await invokeAsyncExport({ ids: productIds, columns: null }, fileName, 'Products With Image');
    };

    const handleExportProductsWithImageSelectedFields = async () => {
        const selectedProductIds = selectedRows.map(row => row.id);
        const fileName = 'Products_With_Image_';
        const fileFormat = 'Products With Image';

        const saved = loadSavedSelectedFields();
        const productIds = await getProductIdsForExport(selectedProductIds);
        const allFields = await ensureAllExportFieldsLoaded();
        const columns = isAllFieldsSelected(saved, allFields) ? null : saved;
        const requestPayload = { ids: productIds, columns };
        await invokeDownload(requestPayload, fileName, fileFormat);
    };

    const openXlsxOptions = (anchorEl: HTMLElement, type: "productList" | "withImage") => {
        const containerRect = popperContentRef.current?.getBoundingClientRect();
        const buttonRect = anchorEl.getBoundingClientRect();

        if (containerRect) {
            setXlsxOptionsPos({
                top: buttonRect.top - containerRect.top,
                left: buttonRect.right - containerRect.left + 8,
            });
        }

        setXlsxOptionsType(type);
        setXlsxOptionsOpen(true);
    };

    const handleXlsxExportClick = async (event: React.MouseEvent<HTMLElement>, type: "productList" | "withImage") => {
        event.preventDefault();
        event.stopPropagation();
        const anchorEl = event.currentTarget as HTMLElement;

        openXlsxOptions(anchorEl, type);
    };

    const handleXlsxOptionsClick = async (type: "productList" | "withImage", mode: "all" | "selected") => {
        if (type === "productList") {
            if (mode === "all") {
                await handleCSVforProductList();
            } else {
                await handleCSVforProductListSelectedFields();
            }
        } else {
            if (mode === "all") {
                await handleExportProductsWithImage();
            } else {
                await handleExportProductsWithImageSelectedFields();
            }
        }
    };

    const selectXlsxOption = (mode: "all" | "selected") => {
        const type = xlsxOptionsType;
        if (!type) return;

        // Close only the submenu immediately (keep main popover open)
        setXlsxOptionsOpen(false);
        setXlsxOptionsType(null);

        void handleXlsxOptionsClick(type, mode);
    };

    const handleExportForInriverPim = async () => {
        let selectedProductIds = selectedRows.map(row => row.id);
        let fileName = 'Inriver_PIM_';

        const productIds = await getProductIdsForExport(selectedProductIds);
        await invokeAsyncExport(productIds, fileName, 'Inriver Pim');
    };

    const handleExportJDEComponentsToExcel = async () => {
        let selectedProductIds = selectedRows.map(row => row.id);
        let fileName = 'JDE_Components_';

        const productIds = await getProductIdsForExport(selectedProductIds);
        await invokeAsyncExport(productIds, fileName, 'JDE Components');
    };

    const handleExportJDESellingLineItemsToExcel = async () => {
        let selectedProductIds = selectedRows.map(row => row.id);
        let fileName = 'JDE_SellingLine_Items_';

        const productIds = await getProductIdsForExport(selectedProductIds);
        await invokeAsyncExport(productIds, fileName, 'JDE SellingLine Items');
    };

    return(
        <>
            <Button
                startIcon={<ExportIcon />}
                onClick = {handlePopper}
            >
                EXPORT
            </Button>
                {<Popper
                    open={openPopper}
                    anchorEl={popperAnchorEl}
                    placement={"bottom-start"}
                    modifiers={[
                        {
                        name: 'flip',
                        enabled: false,
                        },
                        {
                        name: 'preventOverflow',
                        options: {
                            altBoundary: true,
                            tether: false,
                        },
                        },
                    ]}
                    transition
                    >
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout = {250}>
                                <Box 
                                    ref={popperContentRef}
                                    sx={{
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        bgcolor: 'background.paper',
                                        border: 1,
                                        borderColor: 'grey.300',
                                        borderRadius: 1,
                                        alignItems: "flex-start",
                                        padding: 1
                                    }}
                                    onMouseDownCapture={(e) => {
                                        if (!xlsxOptionsOpen) return;
                                        const target = e.target as Node;
                                        const clickedInsideSubmenu = xlsxOptionsRef.current?.contains(target) ?? false;
                                        if (!clickedInsideSubmenu) {
                                            setXlsxOptionsOpen(false);
                                            setXlsxOptionsType(null);
                                        }
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            letterSpacing: 0.6,
                                            color: 'text.secondary',
                                            px: 1,
                                            pt: 0.5,
                                            pb: 0.5,
                                        }}
                                    >
                                        EXPORT PRODUCTS XLSX
                                    </Typography>
                                    <Button
                                        startIcon={<ViewColumnRoundedIcon />}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onOpenChooseColumns();
                                        }}
                                    >
                                        CHOOSE COLUMNS TO EXPORT
                                    </Button>
                                    {/*  Removing Export BOM to CSV JDE from the export list items. */}
                                    {/* <Button
                                        startIcon = {<FileDownloadRoundedIcon/>}
                                        onClick={handleCSVforJDE}
                                    >
                                        Export BOM to CSV JDE
                                    </Button> */}
                                    <Button
                                        startIcon = {<FileDownloadRoundedIcon/>}
                                        onMouseDownCapture={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onClick={(e) => { void handleXlsxExportClick(e, "productList"); }}
                                    >
                                        Export Products to XLSX
                                    </Button>
                                    <Button
                                        startIcon = {<FileDownloadRoundedIcon/>}
                                        onMouseDownCapture={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onClick={(e) => { void handleXlsxExportClick(e, "withImage"); }}
                                    >
                                        Export Products With Thumbnail Image
                                    </Button>

                                    {xlsxOptionsOpen && (
                                        <Box
                                            ref={xlsxOptionsRef}
                                            sx={{
                                                position: 'absolute',
                                                top: xlsxOptionsPos.top,
                                                left: xlsxOptionsPos.left,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                bgcolor: 'background.paper',
                                                border: 1,
                                                borderColor: 'grey.300',
                                                borderRadius: 1,
                                                padding: 1,
                                                minWidth: 160,
                                                zIndex: 2,
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Button
                                                onClick={() => selectXlsxOption("all")}
                                                sx={{ justifyContent: 'flex-start' }}
                                            >
                                                All Fields
                                            </Button>
                                            <Button
                                                onClick={() => selectXlsxOption("selected")}
                                                sx={{ justifyContent: 'flex-start' }}
                                                title='Selected Fields (this will only export columns specified in the “Choose Fields” to export by that user)'
                                            >
                                                Selected Fields
                                            </Button>
                                        </Box>
                                    )}

                                    <Divider sx={{ width: '100%', my: 0.5 }} />

                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            letterSpacing: 0.6,
                                            color: 'text.secondary',
                                            px: 1,
                                            pt: 0.5,
                                            pb: 0.5,
                                        }}
                                    >
                                        EXPORT PRODUCTS OTHER
                                    </Typography>

                                    {sessionService.hasPermission(Permissions.UpdateProduct) 
                                    && <Button
                                        startIcon = {<FactoryRounded/>}
                                        onClick={handleFactorySample}
                                    >
                                        Export Factory Sample Request
                                    </Button>}
                                    <Button
                                        startIcon = {<FileDownloadRoundedIcon/>}
                                        onClick={handleExportForInriverPim}
                                    >
                                        Export to Excel for PIM
                                    </Button>
                                    <Button
                                        startIcon = {<FileDownloadRoundedIcon/>}
                                        onClick={handleExportJDEComponentsToExcel}
                                    >
                                        Export JDE Components To Excel
                                    </Button>
                                    <Button
                                        startIcon = {<FileDownloadRoundedIcon/>}
                                        onClick={handleExportJDESellingLineItemsToExcel}
                                    >
                                        Export JDE SellingLine Items To Excel
                                    </Button>
                                </Box> 
                            </Fade>
                        )}     
                </Popper>}

            </>
    )
};

export default ExportProductListing;